import React from 'react';

import {Alert} from 'src/design/atoms/Alert';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import type {
  CourseDefinitionContentResponse,
  CourseSessionContentResponse} from 'src/api/ProactApiClient';
import {
  CourseDefinitionQuizQuestionTypeEnum
} from 'src/api/ProactApiClient';
import {ContentTitle} from 'src/design/atoms/Typography';
import {Description} from 'src/design/atoms/Description';
import {QuestionAnswerResultIcon} from '../../../../../app/training/sessionIcons';
import {SessionAnswerMultiChoice} from '../courseSession/SessionAnswerMultiChoice';
import {SessionAnswerShowCorrectAnswer} from '../courseSession/SessionAnswerShowCorrectAnswer';
import {SessionAnswerSingleChoice} from '../courseSession/SessionAnswerSingleChoice';
import {GetQuestionInstructionLabel} from '../courseSession/sessionHelpers';

export interface CourseSessionQuizResultsProps {
  dContent: CourseDefinitionContentResponse;
  sContent: CourseSessionContentResponse;
}

export const CourseSessionQuizResults: React.FC<
  CourseSessionQuizResultsProps
> = ({dContent, sContent}) => {
  const dQuiz = dContent.quiz;
  const sQuiz = sContent.quiz;

  if (!dQuiz || !sQuiz) {
    return null;
  }

  const {questions: dQuestions, scorePassPercentage} = dQuiz;
  const {earnedScore, isPassed} = sQuiz;

  let totalScore = 0.0;
  (dQuestions?.map(q => q.scoreValue) ?? []).forEach(score => {
    totalScore += score;
  });
  const scorePercent =
    totalScore > 0 ? Math.round(((earnedScore ?? 0) / totalScore) * 100) : 0;
  const scoreline = `Earned ${
    earnedScore ?? 0
  } pts (${scorePercent}%) out of ${totalScore} pts. Pass requires ${scorePassPercentage}%`;
  const alertColor = isPassed ? 'green' : 'red';

  return (
    <Stack
      ml="xl"
      pl="xl"
      style={{borderLeft: '1px solid var(--mantine-color-gray-3)'}}>
      <Alert color={alertColor}>{scoreline}</Alert>

      {sQuiz.questions?.map((sQuestion, index) => {
        const dQuestion = dQuestions?.find(
          q => q.id === sQuestion.questionDefinitionId
        );
        if (!dQuestion) {
          return null;
        }

        return (
          <React.Fragment key={sQuestion.id ?? `${sContent.id}-q-${index}`}>
            <Group gap="xs" align="flex-start">
              <QuestionAnswerResultIcon
                isCorrect={sQuestion.isAnswersCorrect}
              />
              <ContentTitle text={`${index + 1}. ${dQuestion.labelText}`} />
            </Group>
            <Description mt={4} text={GetQuestionInstructionLabel(dQuestion)} />

            {dQuestion.type ===
              CourseDefinitionQuizQuestionTypeEnum.SingleChoice && (
              <SessionAnswerSingleChoice
                disabled
                error={sQuestion.isAnswersCorrect}
                answers={dQuestion.answers ?? []}
                sessionAnswers={sQuestion.answers ?? []}
                onSessionAnswersChange={() => {}}
              />
            )}

            {dQuestion.type ===
              CourseDefinitionQuizQuestionTypeEnum.MultiChoice && (
              <SessionAnswerMultiChoice
                disabled
                error={sQuestion.isAnswersCorrect}
                answers={dQuestion.answers ?? []}
                sessionAnswers={sQuestion.answers ?? []}
                onSessionAnswersChange={() => {}}
              />
            )}

            <SessionAnswerShowCorrectAnswer
              answers={dQuestion.answers ?? []}
              sessionAnswers={sQuestion.answers ?? []}
              isQuestionCorrect={sQuestion.isAnswersCorrect}
              showCorrectResult={true} // We always want to show the correct answer for admins.
              pronoun="The trainee"
            />
          </React.Fragment>
        );
      })}
    </Stack>
  );
};
